import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

function formatPoints(value: number | string): string {
  const num = parseFloat(String(value));
  if (isNaN(num)) return '0';
  if (Number.isInteger(num)) return String(num);
  return num.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

const getInitial = (name: string) => (name || '?').trim().charAt(0).toUpperCase();

export interface LeaderboardEntry {
  id: number;
  user_id?: number;
  position: number;
  username?: string;
  first_name?: string;
  last_name?: string;
  total_predictions: number;
  correct_predictions: number;
  exact_predictions: number;
  total_points: number;
}

export interface LeaderboardTableProps {
  /** Tournament or match name for header */
  title: string;
  /** Subtitle text (e.g., "Leaderboard", "Match Predictions") */
  subtitle: string;
  /** Optional logo URL */
  logoUrl?: string;
  /** Logo alt text */
  logoAlt?: string;
  /** Leaderboard entries */
  entries: LeaderboardEntry[];
  /** Empty state message */
  emptyMessage?: string;
  /** Additional className */
  className?: string;
  /** When true, skip the outer card wrapper (use inside MainSection) */
  noWrapper?: boolean;
  /**
   * When provided and returns a non-null path, rows render as `<Link>` to that
   * path with `aria-label` describing the participant. Returning `null` keeps
   * the row a non-interactive `<div>` — backward compatible with existing
   * callers that don't opt into row navigation.
   */
  linkBuilder?: (entry: LeaderboardEntry) => string | null;
}

export const LeaderboardTable: React.FC<LeaderboardTableProps> = ({
  title,
  subtitle,
  logoUrl,
  logoAlt,
  entries,
  emptyMessage: emptyMessageProp,
  className,
  noWrapper,
  linkBuilder,
}) => {
  const { t } = useTranslation(['tournament', 'common']);
  const emptyMessage = emptyMessageProp || t('common:noDataYet');

  const content = (
    <>
      <div className={styles.tableHeader}>
        <span className={styles.colRank}>#</span>
        <span className={styles.colName}>{t('tournament:participant')}</span>
        <span className={styles.colStats}>{t('tournament:predictions')}</span>
        <span className={styles.colPoints}>{t('tournament:points')}</span>
      </div>

      <div className={styles.tableBody}>
        {entries.length === 0 ? (
          <div className={styles.emptyState}>
            <p className={styles.emptyStateText}>{emptyMessage}</p>
          </div>
        ) : (
          entries.map((entry, index) => {
            const position = entry.position || index + 1;

            const exactScoreCount = entry.exact_predictions || 0;
            const resultCount = entry.correct_predictions || 0;

            const fullName = `${entry.first_name || ''} ${entry.last_name || ''}`.trim();
            const displayName = fullName ||
              entry.username ||
              `User ${entry.user_id || entry.id}`;

            const totalPoints = Number(entry.total_points) || 0;
            const hasPoints = totalPoints > 0;

            const pointsCellClass = `${styles.colPoints} ${hasPoints ? '' : (styles.zero || '')}`.trim();

            const rowBody = (
              <>
                <span className={styles.colRank}>{position}</span>

                <span className={styles.colName}>
                  <span className={styles.avatar} aria-hidden="true">
                    {getInitial(displayName)}
                  </span>
                  <span className={styles.participantName}>{displayName}</span>
                </span>

                <span className={styles.colStats}>
                  <span className={styles.statRow}>
                    <span className={styles.statLabel}>{t('tournament:exactShort')}:</span>
                    <span className={styles.statValue}>{exactScoreCount}</span>
                  </span>
                  <span className={styles.statRow}>
                    <span className={styles.statLabel}>{t('tournament:resultShort')}:</span>
                    <span className={styles.statValue}>{resultCount}</span>
                  </span>
                </span>

                <span className={pointsCellClass}>
                  <span className={styles.pointsLabel}>
                    {formatPoints(totalPoints)}
                  </span>
                </span>
              </>
            );

            // When linkBuilder returns a non-null path, render the row as a
            // <Link> with screen-reader-friendly aria-label. Otherwise keep the
            // row a plain <div> — backward compatible for callers that don't
            // opt into row navigation.
            const linkTo = linkBuilder ? linkBuilder(entry) : null;
            const rowKey = entry.id || entry.user_id || index;

            if (linkTo) {
              const ariaLabel = t('tournament:participantStats.rowAria', {
                name: displayName,
                rank: position,
                points: formatPoints(totalPoints),
              });

              return (
                <Link
                  key={rowKey}
                  to={linkTo}
                  className={styles.tableRow}
                  data-position={position}
                  data-clickable="true"
                  aria-label={ariaLabel}
                >
                  {rowBody}
                </Link>
              );
            }

            return (
              <div
                key={rowKey}
                className={styles.tableRow}
                data-position={position}
              >
                {rowBody}
              </div>
            );
          })
        )}
      </div>
    </>
  );

  if (noWrapper) return <div className={styles.card}>{content}</div>;

  return (
    <div className={`${styles.main} ${className || ''}`}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          {logoUrl && (
            <img src={logoUrl} alt={logoAlt || ''} className={styles.logo} />
          )}
          <div>
            <h2 className={styles.title}>{title}</h2>
            <p className={styles.subtitle}>{subtitle}</p>
          </div>
        </div>
      </div>
      {content}
    </div>
  );
};

export default LeaderboardTable;
